import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo'
import { isInStorefront } from '../../utils/isInStorefront'

// Devices that can't hover (phones, tablets) reveal controls on tap instead
const TOUCH_QUERY = '(hover: none)'

let Icon = () => (
    <div className="flex py-10 justify-center items-center w-full">
        <FontAwesomeIcon icon={faVideo} size="6x" />
    </div>
)

const useIsTouchDevice = () => {
    const [isTouch, setIsTouch] = useState(
        () => typeof window !== 'undefined' && !!window.matchMedia?.(TOUCH_QUERY).matches
    )

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return

        const mediaQuery = window.matchMedia(TOUCH_QUERY)
        const onChange = e => setIsTouch(e.matches)

        // addListener rather than addEventListener for older Safari support
        mediaQuery.addListener(onChange)
        return () => mediaQuery.removeListener(onChange)
    }, [])

    return isTouch
}

const VideoComponent = ({ state = {}, remove = false }) => {
    const {
        url = '',
        poster = '',
        autoPlay = false,
        loop = false,
        controls = false,
        showControlsOnHover = false,
        muted = false,
        playsInline = false,
    } = state

    const videoRef = useRef(null)
    const [isHovered, setIsHovered] = useState(false)
    const [isTapped, setIsTapped] = useState(false)
    const isTouch = useIsTouchDevice()

    const isInLibraryBlock = typeof remove != 'function'

    const inStorefront = isInStorefront()

    // Browsers only allow muted videos to autoplay, and iOS also requires playsinline.
    // Enforced here as well as in the form so blocks saved before this change still autoplay.
    const isMuted = autoPlay || muted || isInLibraryBlock
    const isPlaysInline = autoPlay || playsInline

    useEffect(() => {
        const video = videoRef.current
        if (!video) return

        // React doesn't reliably reflect the muted prop to the element, which blocks autoplay
        video.muted = isMuted

        if (autoPlay) {
            // play() returns a promise that rejects if the browser still blocks autoplay
            const playPromise = video.play()

            if (playPromise !== undefined) {
                playPromise.catch(() => {})
            }
        }
    }, [autoPlay, isMuted, url])

    if (!url) {
        return inStorefront ? null : <Icon />
    }

    const revealControls = isTouch ? isTapped : isHovered
    const showControls = controls || (showControlsOnHover && revealControls)

    return (
        <div
            className="content__video"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
                if (isTouch && showControlsOnHover) setIsTapped(true)
            }}
        >
            <video
                ref={videoRef}
                style={{ width: '100%' }}
                src={url}
                poster={poster || undefined}
                autoPlay={autoPlay}
                loop={loop}
                muted={isMuted}
                playsInline={isPlaysInline}
                controls={showControls}
                preload={autoPlay ? 'auto' : 'metadata'}
            />
        </div>
    )
}

export default VideoComponent
